import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useRealtime from '../hooks/useRealtime';

const RealtimeNotifications = ({ className = '' }) => {
  const { events, stats, isConnected } = useRealtime({
    pollInterval: 3000, // 3 seconds for more responsive updates
    maxEvents: 10
  });
  const [isExpanded, setIsExpanded] = useState(false);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const eventTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - eventTime) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    return `${Math.floor(diffInSeconds / 3600)}h ago`;
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'user_registered':
        return '👋';
      case 'question_created':
        return '❓';
      default:
        return '📢';
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'user_registered':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'question_created':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const renderEvent = (event) => {
    const { type, data, timestamp } = event;
    
    switch (type) {
      case 'user_registered':
        return (
          <div key={event.id} className={`p-3 rounded-lg border ${getEventColor(type)} animate-slide-in`}>
            <div className="flex items-center gap-3">
              <span className="text-lg">{getEventIcon(type)}</span>
              <div className="flex-1">
                <p className="text-sm font-medium">
                  <span className="font-bold">{data.user.username}</span> just joined StackIt!
                </p>
                <p className="text-xs opacity-75">{formatTimeAgo(timestamp)}</p>
              </div>
              <img 
                src={data.user.avatar} 
                alt={data.user.username}
                className="w-8 h-8 rounded-full"
              />
            </div>
          </div>
        );
      
      case 'question_created':
        return (
          <div key={event.id} className={`p-3 rounded-lg border ${getEventColor(type)} animate-slide-in`}>
            <div className="flex items-center gap-3">
              <span className="text-lg">{getEventIcon(type)}</span>
              <div className="flex-1">
                <p className="text-sm font-medium">
                  <span className="font-bold">{data.question.author}</span> asked:
                </p>
                <Link 
                  to={`/questions/${data.question.id}`}
                  className="text-sm hover:underline line-clamp-2 font-medium"
                >
                  {data.question.title}
                </Link>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex gap-1">
                    {data.question.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs opacity-75">{formatTimeAgo(timestamp)}</p>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (!isConnected || events.length === 0) {
    return null;
  }

  return (
    <div className={`fixed bottom-4 right-4 z-50 ${className}`}>
      {/* Stats indicator */}
      {stats && (
        <div className="mb-2 bg-white rounded-lg shadow-lg border border-gray-200 p-3">
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
              <span className="text-gray-600">Live</span>
            </div>
            <div className="text-gray-600">
              {stats.totalUsers} users • {stats.totalQuestions} questions
            </div>
          </div>
        </div>
      )}

      {/* Notifications panel */}
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-80 max-h-96 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-lg">🔔</span>
              Live Activity
            </h3>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg 
                className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-1">
            {events.length} recent {events.length === 1 ? 'event' : 'events'}
          </p>
        </div>

        {/* Events list */}
        {isExpanded && (
          <div className="max-h-64 overflow-y-auto p-4 space-y-3">
            {events.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No recent activity
              </p>
            ) : (
              events.map(renderEvent)
            )}
          </div>
        )}

        {/* Collapsed preview */}
        {!isExpanded && events.length > 0 && (
          <div className="p-4">
            {renderEvent(events[0])}
            {events.length > 1 && (
              <p className="text-xs text-gray-500 text-center mt-2">
                +{events.length - 1} more {events.length - 1 === 1 ? 'event' : 'events'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RealtimeNotifications; 